import React from "react";
import PropTypes from "prop-types";
import { Button, Stack } from "@mui/material";
import { Icon } from "@iconify/react";
import BaseEventCard from "./BaseEventCard";
import EventMetaChips from "./EventMetaChips";
import CommonEventInfoRows from "./CommonEventInfoRows";
import CapacityIndicator from "./CapacityIndicator";
import TestingRsvpChips from "./TestingRsvpChips";
import EventTagList from "./EventTagList";
import AdminEventActions from "./AdminEventActions";
import PrimaryDetailAction from "./PrimaryDetailAction";
import {
  getDateRangeLabel,
  getEventCardMetrics,
  getManageParticipantsConfig,
  getPublicBookingTooltip,
} from "./cardUtils";
import { eventCardShape } from "./eventPropTypes";

export default function BookingEventCardAdmin({
  event,
  canEdit,
  onEdit,
  onDelete,
  onManageParticipants,
  onDetail,
  onToggleVisibility,
  onCollectFees,
  onOpenPublicBooking,
  onCopyPublicBookingLink,
  onShowPublicBookingQR,
  canOpenMemberBookingDetails,
  onMemberBookingDetail,
}) {
  const metrics = getEventCardMetrics(event);
  const manageConfig = getManageParticipantsConfig(event);
  const publicPageTooltip = getPublicBookingTooltip(event, metrics.isPublicBookingEnabled);
  const copyLinkTooltip = metrics.isPublicBookingEnabled
    ? "Öffentlichen Buchungslink kopieren"
    : publicPageTooltip;
  const qrCodeTooltip = metrics.isPublicBookingEnabled
    ? "QR Code für öffentliche Buchungsseite anzeigen"
    : publicPageTooltip;

  return (
    <BaseEventCard
      accentColor={metrics.accentColor}
      title={event?.eventName || "Unbenannte Veranstaltung"}
      chips={
        <EventMetaChips
          event={event}
          isTesting={metrics.isTesting}
          eventTypeLabel={metrics.eventTypeLabel}
          statusLabel={metrics.statusLabel}
          statusColor={metrics.statusColor}
          accentColor={metrics.accentColor}
        />
      }
      info={
        <CommonEventInfoRows
          event={event}
          dateRange={getDateRangeLabel(event)}
          peopleLabel={metrics.isTesting ? "Aufgestellt" : "Teilnehmer"}
          attendeesCount={metrics.attendeesCount}
          maxParticipants={metrics.maxParticipants}
          icon={metrics.isTesting ? "mdi:account-star" : "mdi:account-group"}
          peopleSuffix={
            metrics.bookingRegistrationCount > 0
              ? ` · Buchungen: ${metrics.bookingRegistrationCount}`
              : ""
          }
        />
      }
      body={
        <>
          <CapacityIndicator
            attendeesCount={metrics.attendeesCount}
            maxParticipants={metrics.maxParticipants}
            capacityPercent={metrics.capacityPercent}
            isFull={metrics.isFull}
            accentColor={metrics.accentColor}
          />
          {metrics.isTesting ? (
            <TestingRsvpChips
              attendeesCount={metrics.attendeesCount}
              rsvpZugesagt={metrics.rsvpZugesagt}
              rsvpAbgesagt={metrics.rsvpAbgesagt}
              rsvpAusstehend={metrics.rsvpAusstehend}
            />
          ) : null}
          <EventTagList event={event} />
        </>
      }
      primaryAction={
        <Stack spacing={0.75}>
          <PrimaryDetailAction onDetail={onDetail} />
          {canOpenMemberBookingDetails ? (
            <Button
              fullWidth
              variant="outlined"
              onClick={onMemberBookingDetail}
              startIcon={<Icon icon="mdi:calendar-plus" />}
            >
              Details und Buchung
            </Button>
          ) : null}
        </Stack>
      }
      showManagement={canEdit}
      managementActions={
        <AdminEventActions
          event={event}
          manageConfig={manageConfig}
          includeBookingActions
          isPublicBookingEnabled={metrics.isPublicBookingEnabled}
          onToggleVisibility={onToggleVisibility}
          onManageParticipants={onManageParticipants}
          onEdit={onEdit}
          onCollectFees={onCollectFees}
          onDelete={onDelete}
          onOpenPublicBooking={onOpenPublicBooking}
          onCopyPublicBookingLink={onCopyPublicBookingLink}
          onShowPublicBookingQR={onShowPublicBookingQR}
          publicPageTooltip={publicPageTooltip}
          copyLinkTooltip={copyLinkTooltip}
          qrCodeTooltip={qrCodeTooltip}
        />
      }
    />
  );
}

BookingEventCardAdmin.propTypes = {
  event: eventCardShape,
  canEdit: PropTypes.bool,
  onEdit: PropTypes.func,
  onDelete: PropTypes.func,
  onManageParticipants: PropTypes.func,
  onDetail: PropTypes.func,
  onToggleVisibility: PropTypes.func,
  onCollectFees: PropTypes.func,
  onOpenPublicBooking: PropTypes.func,
  onCopyPublicBookingLink: PropTypes.func,
  onShowPublicBookingQR: PropTypes.func,
  canOpenMemberBookingDetails: PropTypes.bool,
  onMemberBookingDetail: PropTypes.func,
};

BookingEventCardAdmin.defaultProps = {
  event: null,
  canEdit: false,
  onEdit: undefined,
  onDelete: undefined,
  onManageParticipants: undefined,
  onDetail: undefined,
  onToggleVisibility: undefined,
  onCollectFees: undefined,
  onOpenPublicBooking: undefined,
  onCopyPublicBookingLink: undefined,
  onShowPublicBookingQR: undefined,
  canOpenMemberBookingDetails: false,
  onMemberBookingDetail: undefined,
};
